import {DrupalMedia} from "next-drupal";
import {Button} from "@/components/atoms/button";
import classNames from "classnames";
import {Link} from "@/components/atoms/link";
import {HTMLProps, MutableRefObject, useContext, useRef, useState} from "react";
import {Dictionary} from "@/types/hygiena-types";
import {DictionaryContext} from "@/context/dictionary-context";
import {getDocumentUri} from "@/lib/get-document-uri";
import {useRouter} from "next/router";

/**
 * Displays one or more documents as a button.
 *
 * @param field
 *   The media field.
 * @param label
 *   The label for the button.
 * @param buttonClass
 *   An optional button class.
 * @param callback
 *   An optional callback.
 * @param small
 *   Whether the button is small.
 * @param scrollToLibrary
 *   Scroll to the document library.
 * @param scrollLibraryClass
 *   An optional class to scroll to the resources section.
 * @param hideSmall
 *   Hide on small viewports.
 * @param absolute
 *   Use an absolute path for a link.
 * @param useLabel
 *   Will use the provided document button label if the button is a link.
 * @param defaultList
 *   Will always default to a dropdown when true.
 * @constructor
 */
export function NodeProductDocumentButton({field, label, buttonClass, callback, small = false, scrollToLibrary = false, scrollLibraryClass = "", hideSmall = false, absolute = true, useLabel = false, defaultList = false}: {
  field: DrupalMedia|{uri: string, title: string, options: {[key: string]: any}},
  label: string,
  buttonClass?: string,
  callback?: (boolean) => void,
  small?: boolean,
  scrollToLibrary?: boolean,
  scrollLibraryClass?: string,
  hideSmall?: boolean,
  absolute?: boolean,
  useLabel?: boolean,
  defaultList?: boolean,
}) {
  const t = useContext<Dictionary>(DictionaryContext)
  const {locale, defaultLocale} = useRouter();
  const [show, setShow] = useState<boolean>(false);
  const ref: MutableRefObject<any> = useRef();

  const docLinkProps: HTMLProps<HTMLLinkElement> = locale !== defaultLocale
    ? { rel: 'noindex nofollow'}
    : {};

  function triggerScrollToLibrary() {
    const element = document.getElementById("document-library");
    if (element && scrollLibraryClass?.length) {
      element.scrollIntoView({behavior: 'smooth'})
      // @todo this is purely for the Saftety Datasheet Category for now, refactor as needed if this needs to be
      //   implemented for other categories.
      const select = document.getElementById("document-library--category") as HTMLSelectElement;
      const option = document.getElementById(`document-library--category--${scrollLibraryClass}`) as HTMLOptionElement;
      if (select && option) {
        option.selected = true;
        select.dispatchEvent(new Event('change', { 'bubbles': true }));
      }
    }
  }

  /**
   * Updates the show dropdown value and callback.
   *
   * @param val
   */
  function updateShow(val: boolean) {
    setShow(val);
    callback && callback(val);
  }

  /**
   * Gets the document name.
   *
   * @param document
   */
  function getDocumentName(document: DrupalMedia) {
    if (document?.field_document_button_text?.length) return document.field_document_button_text;
    if (document?.field_document_language?.name) {
      if (document?.field_document_region?.name) {
        return `${document?.field_document_region?.name} - ${document?.field_document_language?.name}`
      }
      return `${document?.field_document_language?.name}`
    }
    return document.name;
  }

  return (
    <>
      {(scrollToLibrary && Array.isArray(field) && field.length) ? (
        <div className={classNames("w-full relative text-center text-primary font-semibold", {"max-md:hidden": hideSmall})} onMouseLeave={() => updateShow(false)} onClick={() => updateShow(!show)}>
          <Button buttonRef={ref} callback={() => updateShow(!show)} icon={"chevron-down"} position={"right"} className={classNames(
            buttonClass ?? "",
            "h-full max-md:text-sm text-center",
            {
              "py-1.5 px-1 w-full": !small,
              "text-xs py-1.5 !px-0.5 w-full": small,
            }
          )} containerClassName="w-full h-full" color={"primary"} hover={{color: "primary"}} responsiveIcon={true}>{label}</Button>
          <ul
            className={classNames(
              "absolute bg-white overflow-auto transition-all w-full z-10 scroll-primary",
              {"max-h-0": !show},
              {"max-h-[250px] transition-all": show}
            )} style={{width: ref?.current?.offsetWidth ?? "auto"}}>
            <li
              onClick={triggerScrollToLibrary}
              className="py-2 border-primary border-2 cursor-pointer border-b-0 first:border-t-0 last:border-b-2 rounded-[2px] leading-tight text-sm bg-white hover:bg-primary hover:text-white transition-all duration-150">
              {`${t?.all?.see_product_library}`}
            </li>
          </ul>
        </div>
      ): <></>}
      {(!scrollToLibrary && Array.isArray(field) && field.length) ? (
        <>
          {/* Field is a link. */}
          {(!defaultList && field.length === 1 && field[0]?.uri && field[0]?.title) && (
            <Button href={getDocumentUri(field[0])} target="_blank" className={classNames(
              buttonClass ?? "",
              "h-full max-md:text-sm text-center",
              {
                "max-md:hidden": hideSmall,
                "py-1.5 px-1": !small,
                "text-xs py-1.5 !px-0.5": small,
              }
            )} containerClassName="h-full" color={"primary"} hover={{color: "primary"}} responsiveIcon={true} {...docLinkProps}>{!useLabel ? field[0].title : label}</Button>
          )}
          {/* Field is a Drupal Media file. */}
          {(!defaultList && field.length === 1 && field[0]?.field_media_file?.uri?.url) && (
            <Button href={getDocumentUri(field[0])} target="_blank" className={classNames(
              buttonClass ?? "",
              "h-full max-md:text-sm text-center",
              {
                "max-md:hidden": hideSmall,
                "py-1.5 px-1": !small,
                "text-xs py-1.5 !px-0.5": small,
              }
            )} containerClassName="h-full" color={"primary"} hover={{color: "primary"}} responsiveIcon={true} {...docLinkProps}>{label}</Button>
          )}
          {defaultList || field.length > 1 ? (
            <div className={classNames("w-full relative text-center text-primary font-semibold", {"max-md:hidden": hideSmall})} onMouseLeave={() => updateShow(false)} onClick={() => updateShow(!show)}>
              <Button buttonRef={ref} callback={() => updateShow(!show)} icon={"chevron-down"} position={"right"} className={classNames(
                buttonClass ?? "",
                "h-full max-md:text-sm text-center",
                {
                  "py-1.5 px-1 w-full": !small,
                  "text-xs py-1.5 !px-0.5 w-full": small,
                }
              )} containerClassName="w-full h-full" color={"primary"} hover={{color: "primary"}} responsiveIcon={true}>{label}</Button>
              <ul
                className={classNames(
                  "absolute bg-white overflow-auto transition-all w-full z-10 scroll-primary",
                  {"max-h-0": !show},
                  {"max-h-[250px] transition-all": show}
                )} style={{width: ref?.current?.offsetWidth ?? "auto"}}>
                {field.map(item => {
                  if (item?.uri && item?.title) {
                    return (
                      <li key={`document-button-${label}--${item.uri}`} className="py-2 border-primary border-2 border-b-0 first:border-t-0 last:border-b-2 rounded-[2px] leading-tight text-sm bg-white hover:bg-primary hover:text-white transition-all duration-150">
                        <Link href={getDocumentUri(item) ?? "/"} target="_blank" className="text-sm hover:text-white w-full block h-full" {...docLinkProps}>
                          {item.title}
                        </Link>
                      </li>
                    )
                  }
                  if (item?.field_media_file?.uri?.url) {
                    return (
                      <li key={`document-button-item-${label}--${item.id}`} className="py-2 border-primary border-2 border-b-0 first:border-t-0 last:border-b-2 rounded-[2px] leading-tight text-sm bg-white hover:bg-primary hover:text-white transition-all duration-150">
                        <Link href={getDocumentUri(item) ?? "/"} target="_blank" className="text-sm hover:text-white w-full block h-full" {...docLinkProps}>
                          {getDocumentName(item)}
                        </Link>
                      </li>
                    )
                  }
                })}
              </ul>
            </div>
          ): []}
        </>
      ) : <></>}
    </>
  )
}

